import React, {
  Dispatch,
  RefObject,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { ProductResponse } from "@/app/api/types/product";
import {
  Check,
  ChevronDown as ChevronDownIcon,
  ChevronRight,
  Loader2,
  MapPin,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { UUID } from "crypto";
import type { StockSummary } from "@/query/hooks/useGlobalStocks";
import type { StockDropdownColumn } from "@/lib/stocks/stockColumns";
import { DropdownPanel, DropdownEmpty } from "./dropdown-primitives";
import { dropdownPanelStyle } from "./dropdown-hooks";
import {
  SearchPendingOverlay,
  useSettleHeightAnimation,
} from "@/components/ui/SearchPendingOverlay";
import { VariantsSubmenu } from "./VariantsSubmenu";
import { getCurrencySymbol } from "@/lib/currency";
import { useUnitMap } from "@/query/hooks/useUnitMap";
import {
  PRODUCT_COMBO_FILTER_IN_STOCK_KEY,
  PRODUCT_COMBO_FILTER_PRICED_KEY,
} from "./useProductSelectState";
import { formatProductMetadata } from "@/lib/products/formatProductMetadata";
import { useProductDisplayName } from "@/hooks/use-product-display-name";

function renderStockCell(val: number | null | undefined) {
  if (val === null || val === undefined) {
    return <span className="text-gray-400">-</span>;
  }
  const cls =
    val <= 0
      ? "text-red-600 bg-red-50 px-1 rounded"
      : "text-green-700 bg-green-50 px-1 rounded";
  return <span className={cls}>{Math.round(val)}</span>;
}

interface ProductDropdownProps {
  isOpen: boolean;
  dropdownPosition: {
    top?: number;
    bottom?: number;
    left: number;
    width: number;
    maxHeight: number;
  };
  openedByButton: boolean;
  searchTerm: string;
  /** True while typed-path results are stale vs. the live search term. */
  isSearchPending: boolean;
  setSearchTerm: (term: string) => void;
  setIsManuallyNavigating: (v: boolean) => void;
  setSelectedDropdownIndex: (idx: number) => void;
  handleKeyDown: (event: React.KeyboardEvent<HTMLInputElement>) => void;
  allowCustom: boolean;
  isCustomActive: boolean;
  setIsOpen: (open: boolean) => void;
  setCustomDialogOpen: (open: boolean) => void;
  isEnriched: boolean;
  priceLookup?: (productId: string) => number | null;
  stockLookup?: (productId: string) => StockSummary | null;
  stockLocationLabel?: string | null;
  stockLocations?: Array<{ id: string; name: string; code: string | null }>;
  localLocationId: string | null;
  setLocalLocationId: (id: string | null) => void;
  dropdownColumns: StockDropdownColumn[];
  filterInStock: boolean;
  setFilterInStock: Dispatch<SetStateAction<boolean>>;
  filterPriced: boolean;
  setFilterPriced: (v: boolean) => void;
  filterCounts: {
    total: number;
    inStock: number;
    priced: number;
    withoutInStock: number;
    withoutPriced: number;
  };
  setRenderPage: (fn: (prev: number) => number) => void;
  showProductMetadata: boolean;
  currentProductDetails: ProductResponse | null;
  value: UUID | null;
  selectedDropdownIndex: number;
  productsToRender: ProductResponse[];
  productsLoading: boolean;
  filteredProducts: ProductResponse[];
  hasMoreRenderPages: boolean;
  skuInput: string;
  getProductChildren: (productId: UUID) => ProductResponse[];
  onProductSelect: (
    product: ProductResponse,
    subproductId?: UUID | null,
  ) => void;
  onSubproductSelect: (product: ProductResponse, subproductId: UUID) => void;
  // Submenu state
  hoveredProductId: UUID | null;
  setHoveredProductId: (id: UUID | null) => void;
  submenuPosition: { top: number; left: number } | null;
  setSubmenuPosition: (pos: { top: number; left: number } | null) => void;
  allProducts: ProductResponse[];
  allProductsWithChildren: ProductResponse[];
  // Refs
  dropdownRef: RefObject<HTMLDivElement | null>;
  searchInputRef: RefObject<HTMLInputElement | null>;
  observerTarget: RefObject<HTMLDivElement | null>;
}

export const ProductDropdown = ({
  isOpen,
  dropdownPosition,
  openedByButton,
  searchTerm,
  isSearchPending,
  setSearchTerm,
  setIsManuallyNavigating,
  setSelectedDropdownIndex,
  handleKeyDown,
  allowCustom,
  isCustomActive,
  setIsOpen,
  setCustomDialogOpen,
  isEnriched,
  priceLookup,
  stockLookup,
  stockLocationLabel,
  stockLocations,
  localLocationId,
  setLocalLocationId,
  dropdownColumns,
  filterInStock,
  setFilterInStock,
  filterPriced,
  setFilterPriced,
  filterCounts,
  setRenderPage,
  showProductMetadata,
  currentProductDetails,
  value,
  selectedDropdownIndex,
  productsToRender,
  productsLoading,
  filteredProducts,
  hasMoreRenderPages,
  skuInput,
  getProductChildren,
  onProductSelect,
  onSubproductSelect,
  hoveredProductId,
  setHoveredProductId,
  submenuPosition,
  setSubmenuPosition,
  allProducts,
  allProductsWithChildren,
  dropdownRef,
  searchInputRef,
  observerTarget,
}: ProductDropdownProps) => {
  const { t } = useTranslation();
  const currency = getCurrencySymbol();

  const unitMap = useUnitMap();
  const displayName = useProductDisplayName();

  useEffect(() => {
    if (!isOpen || !openedByButton) return;
    const raf = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(raf);
  }, [isOpen, openedByButton, searchInputRef]);

  const handleFilterInStockChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const checked = e.target.checked;
      setFilterInStock(checked);
      localStorage.setItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY, String(checked));
      setRenderPage(() => 0);
    },
    [setFilterInStock, setRenderPage],
  );

  const clearInStockFilter = useCallback(() => {
    setFilterInStock(false);
    localStorage.setItem(PRODUCT_COMBO_FILTER_IN_STOCK_KEY, "false");
    setRenderPage(() => 0);
  }, [setFilterInStock, setRenderPage]);

  const clearPricedFilter = useCallback(() => {
    setFilterPriced(false);
    localStorage.setItem(PRODUCT_COMBO_FILTER_PRICED_KEY, "false");
    setRenderPage(() => 0);
  }, [setFilterPriced, setRenderPage]);

  const clearBothFilters = useCallback(() => {
    clearInStockFilter();
    clearPricedFilter();
  }, [clearInStockFilter, clearPricedFilter]);

  // Scrollable product list — reset to the top whenever a fresh result set
  // lands (`filteredProducts` only changes reference on a settled search /
  // filter change, never on render-page pagination), so a new search always
  // starts at the first match instead of stranding the user mid-list.
  const listScrollRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!isOpen) return;
    if (listScrollRef.current) listScrollRef.current.scrollTop = 0;
  }, [filteredProducts, isOpen]);

  // While a search is pending the list shows STALE rows under a veil — pin
  // the wrapper's height so nothing moves under the cursor, then animate to
  // the new natural height when the settled results land.
  const settleWrapRef = useRef<HTMLDivElement>(null);
  useSettleHeightAnimation(settleWrapRef, isSearchPending);

  if (!isOpen) return null;

  const renderEnrichedCols = (productId: string) => {
    if (!isEnriched) return null;

    const price = priceLookup?.(productId);
    const stock = stockLookup?.(productId);

    return (
      <div className="flex items-center gap-2 flex-shrink-0">
        {priceLookup && (
          <div className="w-16 text-center text-xs tabular-nums">
            {price != null ? (
              `${Number(price).toFixed(2)}${currency}`
            ) : (
              <span className="text-gray-400">-</span>
            )}
          </div>
        )}
        {stockLookup && (
          <>
            <div className="w-16 text-right text-xs tabular-nums ml-1">
              {renderStockCell(stock?.quantity)}
              {(() => {
                const unitAbbr = stock?.unit_id
                  ? unitMap.get(stock.unit_id)
                  : null;
                return unitAbbr ? (
                  <span className="text-[10px] text-gray-400 ml-0.5">
                    {unitAbbr}
                  </span>
                ) : null;
              })()}
            </div>
            {dropdownColumns.map((col) => (
              <div
                key={col.key}
                className="w-10 text-center text-xs tabular-nums"
              >
                {renderStockCell(stock?.metadata?.[col.key])}
              </div>
            ))}
          </>
        )}
      </div>
    );
  };

  const currentItem =
    currentProductDetails && currentProductDetails.id === value
      ? currentProductDetails
      : undefined;

  const showCurrentItem = currentItem && openedByButton && !searchTerm.trim();

  return (
    <>
      <div
        data-recap-portal
        className="fixed inset-0 z-[9998]"
        onClick={() => {
          setIsOpen(false);
          setHoveredProductId(null);
          setSubmenuPosition(null);
        }}
      />
      <DropdownPanel
        ref={dropdownRef}
        data-testid="product-dropdown"
        className="fixed z-[9999] overflow-hidden flex flex-col"
        style={dropdownPanelStyle(dropdownPosition)}
      >
        {/* Search bar when opened by button */}
        {openedByButton && (
          <div className="p-2 border-b border-gray-100">
            <div className="relative">
              <input
                ref={searchInputRef}
                type="text"
                placeholder={t("purchases.searchProducts")}
                value={searchTerm}
                onChange={(e) => {
                  setSearchTerm(e.target.value);
                  setIsManuallyNavigating(false);
                  setSelectedDropdownIndex(0);
                }}
                onKeyDown={handleKeyDown}
                data-testid="product-search-input"
                className={`w-full px-2 py-1 text-sm border border-gray-200 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 ${
                  isSearchPending ? "pr-7" : ""
                }`}
              />
              {/* Search-pending spinner — results on screen are stale vs. the
                  live term (debounce settling or a time-sliced search in
                  flight). Mirrors the sender/recipient picker's indicator. */}
              {isSearchPending && (
                <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center">
                  <Loader2
                    data-testid="product-search-spinner"
                    className="h-3.5 w-3.5 animate-spin text-gray-400"
                  />
                </div>
              )}
            </div>
          </div>
        )}

        {/* Custom entry */}
        {allowCustom && (
          <button
            type="button"
            className={`w-full px-3 py-2 text-left flex items-center gap-2 hover:bg-orange-50 transition-colors border-b border-dashed border-orange-200 ${
              isCustomActive ? "bg-orange-50" : ""
            }`}
            onClick={() => {
              setIsOpen(false);
              setCustomDialogOpen(true);
            }}
          >
            <MapPin className="h-4 w-4 text-orange-500 flex-shrink-0" />
            <div className="flex-1 min-w-0">
              <div className="font-medium text-sm text-orange-700">
                {t("products.custom", "Custom")}
              </div>
              <div className="text-xs text-orange-500">
                {t(
                  "products.customDestinationDescription",
                  "Enter custom destination details",
                )}
              </div>
            </div>
          </button>
        )}

        {/* Enriched header row */}
        {isEnriched && (
          <div className="flex items-center gap-2 px-3 py-1 border-b border-gray-200 bg-gray-50 text-[10px] font-medium text-gray-500 uppercase tracking-wide">
            <div className="flex-1 min-w-0 flex items-center gap-2">
              {stockLookup && (
                <label
                  className="flex items-center gap-1 cursor-pointer normal-case tracking-normal text-[10px] text-gray-500 hover:text-gray-700"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    data-testid="product-dropdown-stock-filter"
                    checked={filterInStock}
                    onChange={handleFilterInStockChange}
                    className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {t("products.inStock")}{" "}
                  <span className="tabular-nums text-gray-400">
                    ({filterCounts.inStock})
                  </span>{" "}
                  <span className="text-gray-400">(Shift+S)</span>
                </label>
              )}
              {priceLookup && (
                <label
                  className="flex items-center gap-1 cursor-pointer normal-case tracking-normal text-[10px] text-gray-500 hover:text-gray-700"
                  onMouseDown={(e) => e.stopPropagation()}
                >
                  <input
                    type="checkbox"
                    checked={filterPriced}
                    onChange={(e) => {
                      const checked = e.target.checked;
                      setFilterPriced(checked);
                      localStorage.setItem(
                        PRODUCT_COMBO_FILTER_PRICED_KEY,
                        String(checked),
                      );
                      setRenderPage(() => 0);
                    }}
                    className="h-3 w-3 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  {t("products.priced")}{" "}
                  <span className="tabular-nums text-gray-400">
                    ({filterCounts.priced})
                  </span>{" "}
                  <span className="text-gray-400">(Shift+P)</span>
                </label>
              )}
              {/* Location selector — after checkboxes */}
              {stockLocations && stockLocations.length > 0 ? (
                <StockLocationSelect
                  locations={stockLocations}
                  locationId={localLocationId}
                  onChange={setLocalLocationId}
                />
              ) : stockLocationLabel ? (
                <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 normal-case tracking-normal text-[10px] font-medium whitespace-nowrap">
                  {t("products.stockAtLocation", {
                    location: stockLocationLabel,
                  })}
                </span>
              ) : null}
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {priceLookup && (
                <div className="w-16 text-center">{t("products.price")}</div>
              )}
              {stockLookup && (
                <>
                  <div className="w-16 text-right ml-1">
                    {t("products.quantity")}
                  </div>
                  {dropdownColumns.map((col) => (
                    <div key={col.key} className="w-10 text-center">
                      {col.label}
                    </div>
                  ))}
                </>
              )}
            </div>
            <div className="w-4 flex-shrink-0" />
          </div>
        )}

        {/* The wrapper (not the scrollable list) carries the searching veil
            and the settle-height animation: while a search is pending the
            stale rows sit frozen under the overlay — no clicks land, nothing
            resizes — then the list animates to its new height on settle. */}
        <div
          ref={settleWrapRef}
          className="relative flex-1 min-h-0 flex flex-col"
        >
          <div ref={listScrollRef} className="flex-1 min-h-0 overflow-y-auto">
            {/* Currently selected item pinned at top */}
            {showCurrentItem && (
              <>
                <div
                  data-dropdown-item
                  className="px-3 py-1.5 hover:bg-gray-50 cursor-pointer flex items-center gap-2 bg-blue-50 border-b border-blue-100 relative"
                  onMouseEnter={(e) => {
                    const currentItemChildren = getProductChildren(
                      currentItem.id,
                    );
                    const currentItemParents = currentItem.parents ?? [];
                    const hasVariants =
                      currentItemChildren.length > 0 ||
                      currentItemParents.length > 0;

                    if (hasVariants) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredProductId(currentItem.id);
                      setSubmenuPosition({
                        top: rect.top,
                        left: rect.right,
                      });
                    }
                  }}
                  onMouseLeave={() => {}}
                  onMouseDown={(e) => {
                    const currentItemChildren = getProductChildren(
                      currentItem.id,
                    );
                    const currentItemParents = currentItem.parents ?? [];
                    const hasVariants =
                      currentItemChildren.length > 0 ||
                      currentItemParents.length > 0;
                    if (!hasVariants) {
                      e.preventDefault();
                      onProductSelect(currentItem);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0 flex items-center gap-1">
                    <Check className="h-4 w-4 text-blue-600 flex-shrink-0" />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-baseline gap-2 min-w-0">
                        <div className="text-xs font-semibold text-gray-700 flex-shrink-0">
                          {currentItem.sku}
                        </div>
                        <div className="text-xs text-gray-900 truncate min-w-0">
                          {displayName(currentItem)}
                        </div>
                      </div>
                      {showProductMetadata &&
                        formatProductMetadata(currentItem.product_metadata) && (
                          <div className="text-xs text-gray-600 truncate">
                            {formatProductMetadata(
                              currentItem.product_metadata,
                            )}
                          </div>
                        )}
                    </div>
                  </div>
                  {renderEnrichedCols(currentItem.id)}
                  <div className="w-4 flex-shrink-0 flex items-center justify-center">
                    {(getProductChildren(currentItem.id).length > 0 ||
                      (currentItem.parents?.length ?? 0) > 0) && (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
                {productsToRender.length > 0 && (
                  <div className="border-b border-gray-100"></div>
                )}
              </>
            )}

            {/* Product list */}
            {productsToRender.map((product, index) => {
              const actualIndex = showCurrentItem ? index + 1 : index;
              const isSelected = selectedDropdownIndex === actualIndex;
              const productChildren = getProductChildren(product.id);
              const hasSubproducts = productChildren.length > 0;

              return (
                <div
                  key={product.id.toString()}
                  data-dropdown-item
                  data-testid="product-option"
                  data-product-id={product.id}
                  className={`relative px-3 py-1.5 cursor-pointer flex items-center gap-2 ${
                    isSelected
                      ? "bg-blue-100 border-l-2 border-blue-500"
                      : "hover:bg-gray-50"
                  }`}
                  onMouseEnter={(e) => {
                    if (hasSubproducts) {
                      const rect = e.currentTarget.getBoundingClientRect();
                      setHoveredProductId(product.id);
                      setSubmenuPosition({
                        top: rect.top,
                        left: rect.right,
                      });
                    }
                  }}
                  onMouseLeave={() => {}}
                  onMouseDown={(e) => {
                    if (!hasSubproducts) {
                      e.preventDefault();
                      onProductSelect(product);
                    }
                  }}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2 min-w-0">
                      <div className="text-xs font-semibold text-gray-700 flex-shrink-0">
                        {product.sku}
                      </div>
                      <div className="text-xs text-gray-900 truncate min-w-0">
                        {displayName(product)}
                      </div>
                    </div>
                    {showProductMetadata &&
                      formatProductMetadata(product.product_metadata) && (
                        <div className="text-xs text-gray-600 truncate">
                          {formatProductMetadata(product.product_metadata)}
                        </div>
                      )}
                  </div>
                  {renderEnrichedCols(product.id)}
                  <div className="w-4 flex-shrink-0 flex items-center justify-center">
                    {hasSubproducts && (
                      <ChevronRight className="h-4 w-4 text-gray-400" />
                    )}
                  </div>
                </div>
              );
            })}

            {/* Render-window sentinel. Grows the client-side slice only — the
                catalog arrives in one shot from the global cache, there is no
                REST page behind this. */}
            {hasMoreRenderPages && (
              <div ref={observerTarget} className="px-3 py-2 text-center">
                <div className="h-4 w-4 mx-auto animate-spin border-2 border-gray-300 border-t-blue-600 rounded-full" />
              </div>
            )}

            {!productsLoading &&
              filteredProducts.length === 0 &&
              (() => {
                const stockActive = filterInStock && !!stockLookup;
                const pricedActive = filterPriced && !!priceLookup;
                const hasActiveFilter = stockActive || pricedActive;
                // Products match the search but the filters hid them all — offer
                // a way out with the count each undo would surface. When nothing
                // matches the search itself, fall back to the plain message.
                if (hasActiveFilter && filterCounts.total > 0) {
                  return (
                    <div className="px-3 py-3 text-sm text-gray-600">
                      <div className="mb-2">{t("products.filtersHideAll")}</div>
                      <div className="flex flex-row flex-wrap gap-2">
                        {pricedActive && (
                          <button
                            type="button"
                            data-testid="product-dropdown-undo-priced"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clearPricedFilter}
                            className="rounded-full px-3 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                          >
                            {t("products.undoPricedFilter", {
                              count: filterCounts.withoutPriced,
                            })}
                          </button>
                        )}
                        {stockActive && (
                          <button
                            type="button"
                            data-testid="product-dropdown-undo-stock"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clearInStockFilter}
                            className="rounded-full px-3 py-1 text-xs text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                          >
                            {t("products.undoStockFilter", {
                              count: filterCounts.withoutInStock,
                            })}
                          </button>
                        )}
                        {stockActive && pricedActive && (
                          <button
                            type="button"
                            data-testid="product-dropdown-undo-both"
                            onMouseDown={(e) => e.preventDefault()}
                            onClick={clearBothFilters}
                            className="rounded-full px-3 py-1 text-xs font-medium text-blue-700 bg-blue-50 hover:bg-blue-100 transition-colors"
                          >
                            {t("products.undoBothFilters", {
                              count: filterCounts.total,
                            })}
                          </button>
                        )}
                      </div>
                    </div>
                  );
                }
                if (searchTerm || skuInput || filterInStock || filterPriced) {
                  return (
                    <DropdownEmpty className="px-3 py-2 text-sm">
                      {t("purchases.noProductsFound")}
                    </DropdownEmpty>
                  );
                }
                return null;
              })()}
          </div>
          <SearchPendingOverlay
            pending={isSearchPending}
            label={t("common.searching")}
            testId="product-dropdown-searching"
          />
        </div>
      </DropdownPanel>

      {/* Submenu for variants */}
      <VariantsSubmenu
        hoveredProductId={hoveredProductId}
        submenuPosition={submenuPosition}
        allProducts={allProducts}
        allProductsWithChildren={allProductsWithChildren}
        getProductChildren={getProductChildren}
        onProductSelect={onProductSelect}
        setHoveredProductId={setHoveredProductId}
        setSubmenuPosition={setSubmenuPosition}
      />
    </>
  );
};

// ── Inline stock location selector for the enriched header ──────────

function StockLocationSelect({
  locations,
  locationId,
  onChange,
}: {
  locations: Array<{ id: string; name: string; code: string | null }>;
  locationId: string | null;
  onChange: (locationId: string | null) => void;
}) {
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const selectedLabel = useMemo(() => {
    if (!locationId) return t("ordersRecap.allLocations", "All");
    const loc = locations.find((l) => l.id === locationId);
    if (!loc) return t("ordersRecap.allLocations", "All");
    return loc.code || loc.name;
  }, [locationId, locations, t]);

  const items = useMemo(() => {
    const list: Array<{ id: string | null; label: string }> = [
      { id: null, label: t("ordersRecap.noLocation", "All locations") },
    ];
    const s = search.toLowerCase().trim();
    for (const loc of locations) {
      const label = loc.code ? `${loc.code} - ${loc.name}` : loc.name;
      if (s && !label.toLowerCase().includes(s)) continue;
      list.push({ id: loc.id, label });
    }
    return list;
  }, [locations, search, t]);

  // Close on outside click
  React.useEffect(() => {
    if (!open) return;
    requestAnimationFrame(() => searchRef.current?.focus());
    const handleClick = (e: MouseEvent) => {
      if (!containerRef.current?.contains(e.target as Node)) {
        setOpen(false);
        setSearch("");
      }
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  return (
    <div
      ref={containerRef}
      className="relative"
      onMouseDown={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded bg-blue-50 text-blue-700 hover:bg-blue-100 transition-colors normal-case tracking-normal text-[10px] font-medium max-w-[100px] cursor-pointer"
        title={selectedLabel}
        onClick={(e) => {
          e.stopPropagation();
          e.preventDefault();
          setOpen(!open);
        }}
      >
        <MapPin className="h-2.5 w-2.5 flex-shrink-0" />
        <span className="truncate">{selectedLabel}</span>
        <ChevronDownIcon
          className={`h-2.5 w-2.5 flex-shrink-0 transition-transform ${open ? "rotate-180" : ""}`}
        />
      </button>
      {open && (
        <div className="absolute top-full left-0 mt-1 w-52 bg-white border border-gray-200 rounded-md shadow-lg z-[10001]">
          <div className="p-1.5 border-b">
            <input
              ref={searchRef}
              type="text"
              className="w-full h-7 px-2 text-xs border border-gray-200 rounded bg-gray-50/80 focus:bg-white focus:border-blue-300 focus:ring-1 focus:ring-blue-100 focus:outline-none"
              placeholder={t(
                "ordersRecap.searchLocation",
                "Search location...",
              )}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Escape") {
                  setOpen(false);
                  setSearch("");
                }
                e.stopPropagation();
              }}
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto">
            {items.length === 0 ? (
              <div className="p-2 text-xs text-center text-gray-400">
                {t("ordersRecap.noLocationFound", "No location found.")}
              </div>
            ) : (
              items.map((item) => (
                <div
                  key={item.id ?? "none"}
                  className={`flex items-center justify-between px-2 py-1.5 cursor-pointer text-xs transition-colors hover:bg-gray-50 ${
                    item.id === null ? "text-gray-400" : ""
                  }`}
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    onChange(item.id);
                    setOpen(false);
                    setSearch("");
                  }}
                >
                  <span className="truncate">{item.label}</span>
                  {locationId === item.id && (
                    <Check className="h-3 w-3 shrink-0 text-blue-500 ml-2" />
                  )}
                </div>
              ))
            )}
          </div>
        </div>
      )}
    </div>
  );
}
